import { AvailableStreamsCarousel } from 'components/availableStreamsCarousel';
import { BannerHeroRenderer } from 'components/banner/bannerHero';
import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { DirettissimeCarousel } from 'components/direttissimeCarousel';
import { HeaderSticky } from 'components/headerSticky';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { EmptySport, Loader } from 'components/scommessaFallback';
import { SportNews } from 'components/sportNews/SportNews';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { selectIsSportSearchEnabled } from 'features/configuration/selectors';
import { selectDisciplinaBySlug } from 'features/live/liveNav/selectors';
import { ScommesseLayout } from 'features/scommesseLayout';
import { decodePathname, selectIsFilterChanged, selectMenuManifestazioni } from 'features/sport';
import { SportComposer } from 'features/sport/components/sportComposer';
import { SportInternalLayout } from 'features/sport/components/sportInternalLayout';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { useScommessaStatus } from 'features/sport/hooks';
import { resetFilterAvvenimento, resetMenuManifestazioniFilters } from 'features/sport/sportSlice';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { SportDisciplinaPageQuery } from 'lib/datoCms/queries/getSportDisciplinaPage';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef } from 'react';
import { Category } from 'types/categoryEnum';
import { isSnaiSite } from 'utility/constant';
import { isTruthy } from 'utility/functions';

type Props = SportDisciplinaPageQuery & {
  taxes?: Taxes;
  seoData?: TabsWithAccordionType;
};

const PageContent = ({ taxes, sportNav, seoData, allCarrelloErrors, sportDisciplinaPage }: Props) => {
  const { id, visualComposer, heroBanner } = sportDisciplinaPage ?? {};
  const { sportNewsBlock, guardaSuSnaiBlock, direttissimeBlock } = sportDisciplinaPage ?? {};

  const { asPath } = useRouter();
  const { slug, disciplina } = useMemo(() => {
    const slug = decodePathname(asPath);
    const [_, disciplina] = slug.split('/');
    return { slug, disciplina };
  }, [asPath]);

  const dispatch = useAppDispatch();
  const { isMobile } = useWindowSize();

  const [sportTrKey] = ['sport'];

  const refContainer = useRef<HTMLDivElement>(null);

  const [sportHeaderStickyLabel] = useGetLabelsByKeys(['sport-header-sticky']);

  const { descrizioneDisciplina, descrizioneDisciplinaTrKey } = useTypedSelector(selectMenuManifestazioni);
  const disciplinaFromMenu = useTypedSelector((st) => selectDisciplinaBySlug(st, disciplina));

  const { des, desTrKey } = useMemo(() => {
    const { descrizione, descrizioneTrKey } = disciplinaFromMenu ?? {};

    const des = descrizione ?? descrizioneDisciplina ?? decodeURI(disciplina);
    const desTrKey = descrizioneTrKey ?? descrizioneDisciplinaTrKey;

    return {
      des: (des ?? disciplina).toLowerCase(),
      desTrKey,
    };
  }, [disciplina, disciplinaFromMenu, descrizioneDisciplina, descrizioneDisciplinaTrKey]);

  const { isLoading } = useScommessaStatus();

  const isSportSearchEnabled = useTypedSelector(selectIsSportSearchEnabled);

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [dispatch, allCarrelloErrors]);

  const isFilterChanged = useTypedSelector(selectIsFilterChanged);
  const resetFilters = () => {
    dispatch(resetFilterAvvenimento());
    dispatch(resetMenuManifestazioniFilters());
  };

  return (
    <ScommesseLayout taxes={taxes} disciplina={disciplina} disciplinaTrKey={desTrKey} hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={'Sport'}
        category={Category.Sport}
        titleTrKey={sportTrKey}
        labelSearch={isSportSearchEnabled ? 'Cerca' : null}
        hasPaddingLeft={isMobile}
        titleHighlighted={des}
        titleHighlightedTrKey={desTrKey}
      />
      {isTruthy(heroBanner?.[0]?.banners?.length) && <BannerHeroRenderer pageId={id} heroBanner={heroBanner?.[0]} />}
      <SportTopNavigation hasFirstElementFixed={isSnaiSite} />
      <BannerHTML5 url={enBannerHTML5.Sport} />
      <HeaderSticky label={sportHeaderStickyLabel} />
      <SportInternalLayout sportNav={sportNav} disciplina={disciplina} disciplinaTrKey={desTrKey}>
        <div ref={refContainer}>
          {isTruthy(visualComposer?.length) && (
            <SportComposer
              key={`Composer-${slug}`}
              pageId={id}
              cmsData={visualComposer ?? []}
              templateSlug="templateDisciplina"
            />
          )}
          <Loader key={`loader-${id}-${isLoading}-${asPath}`} />
        </div>
        <EmptySport
          key={`empty-${id}-${isLoading}-${asPath}`}
          container={refContainer.current}
          hasFilter={isFilterChanged}
          hasRedirect={true}
          resetFilter={resetFilters}
        />
        {guardaSuSnaiBlock && (
          <AvailableStreamsCarousel
            title={guardaSuSnaiBlock.title}
            className="inEvidenzaContainer"
            noPaddingRight={isMobile}
          />
        )}
        {direttissimeBlock && (
          <DirettissimeCarousel
            title={direttissimeBlock.title}
            className="direttissimeContainer"
            slug={slug}
            isEnabledPromo={direttissimeBlock.promoEnabled}
          />
        )}
        {sportNewsBlock && <SportNews url={sportNewsBlock?.url} title={sportNewsBlock.title} />}
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </SportInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
